import { useEffect } from 'react';
import { useRouter } from 'next/router';

const HISTORY_STACK_SESSION_STORAGE_KEY = 'history-stack';
const CURRENT_HISTORY_PAGE_INDEX_KEY = 'current-history-index';

type History = {
  key: string;
  href: string;
};

const getHistoryStack = () => {
  const storageData = sessionStorage.getItem(HISTORY_STACK_SESSION_STORAGE_KEY);
  return (storageData ? JSON.parse(storageData) : []) as History[];
};

const setHistoryStack = (stack: History[]) =>
  sessionStorage.setItem(
    HISTORY_STACK_SESSION_STORAGE_KEY,
    JSON.stringify(stack),
  );

const getCurrentHistoryIndex = () => {
  const storageData = sessionStorage.getItem(CURRENT_HISTORY_PAGE_INDEX_KEY);
  return (storageData ? JSON.parse(storageData) : 0) as number;
};

const setCurrentHistoryIndex = (index: number) =>
  sessionStorage.setItem(CURRENT_HISTORY_PAGE_INDEX_KEY, `${index}`);

export const getHistoryIndexByKey = (key: string) => {
  const historyIndex = getHistoryStack().findIndex(
    (history) => history.key === key,
  );

  return historyIndex === -1 ? 0 : historyIndex;
};

const getHistoryLength = () => {
  return getHistoryStack().length;
};

const hasHistoryIndex = (key: string) => {
  return getHistoryStack().some((history) => history.key === key);
};

const useHistoryIndexChangeByRoute = () => {
  const router = useRouter();

  useEffect(() => {
    const updateHistory = () => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const key = window.history.state?.key as string | undefined;
      const { href } = window.location;

      if (!key) {
        return;
      }

      let historyStack = getHistoryStack();
      const currentHistoryIndex = getCurrentHistoryIndex();

      const currentHistory = historyStack[currentHistoryIndex] as
        | History
        | undefined;

      // 새로 고침을 수행함
      if (currentHistory?.href === href) {
        return;
      }

      // 이전 히스토리로 돌아옴
      if (hasHistoryIndex(key)) {
        setCurrentHistoryIndex(getHistoryIndexByKey(key));
      } else {
        // 새로운 히스토리가 쌓임
        // 현재 히스토리 이후 히스토리들을 제거
        if (currentHistoryIndex + 1 < getHistoryLength()) {
          historyStack = historyStack.slice(0, currentHistoryIndex + 1);
        }

        historyStack.push({
          key,
          href,
        });

        setHistoryStack(historyStack);
        setCurrentHistoryIndex(historyStack.length - 1);
      }
    };

    updateHistory();

    const handleRouteChangeComplete = () => {
      updateHistory();
    };

    router.events.on('routeChangeComplete', handleRouteChangeComplete);

    // Cleanup
    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useHistoryIndexChangeByRoute;
